import React from "react";

import {
  GoalBasedFinancingRecord,
  GoalBasedGoalType,
} from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import {
  GoalBasedPlanDateResponse,
  GoalBasedRepeatTimeFrame,
} from "@fartherfinance/frontend/api/FinancialPlans/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import FilledCard from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FilledCard/FilledCard";
import { getDateRangeStringFromPlanDates } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/helpers/getDateRangeStringFromPlanDates";
import { ClientAndSpouseDetails } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/hooks/useGetClientAndSpouseDetails";
import { FinancingMethod } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/Types";
import { assertUnreachable } from "@src/utils/assertUnreachable";
import toUSD from "@src/utils/toUSD";

type GoalCardSubHeaderLabel =
  | "Amount"
  | "Annual Amount"
  | "Annual Tuition"
  | "Purchase Price"
  | "Target Amount";

const getSubHeaderLabel = (
  goalType: GoalBasedGoalType
): GoalCardSubHeaderLabel => {
  switch (goalType) {
    case "AssetPurchase":
      return "Purchase Price";
    case "CashReserve":
      return "Target Amount";
    case "Education":
      return "Annual Tuition";
    case "Gift":
      return "Amount";
    case "HomeImprovement":
      return "Amount";
    case "HomePurchase":
      return "Purchase Price";
    case "InvestmentProperty":
      return "Purchase Price";
    case "Land":
      return "Purchase Price";
    case "Legacy":
      return "Target Amount";
    case "Other":
      return "Amount";
    case "Retirement":
      return "Amount";
    case "Vacation":
      return "Annual Amount";
    case "VacationHome":
      return "Purchase Price";
    case "Vehicle":
      return "Purchase Price";
    case "Wedding":
      return "Amount";
    default:
      return assertUnreachable(goalType);
  }
};

interface Props {
  heading: string;
  endDate: GoalBasedPlanDateResponse | undefined;
  financeMethod: FinancingMethod;
  financing?: GoalBasedFinancingRecord;
  amount: string | number;
  clientFullName: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  startDate: GoalBasedPlanDateResponse | undefined;
  clientAndSpouse: ClientAndSpouseDetails | undefined;
  className?: string;
  repeats: GoalBasedRepeatTimeFrame | undefined;
  titleSubScript?: string;
  goalType: GoalBasedGoalType;
  owner: ClientId;
}

const unknown = "unknown";

const FilledGoalCard: React.FC<Props> = ({
  startDate,
  heading,
  endDate,
  amount,
  clientFullName,
  onClick,
  clientAndSpouse,
  className,
  repeats,
  titleSubScript,
  goalType,
  owner,
}) => {
  const dateText = getDateRangeStringFromPlanDates({
    startDate,
    endDate,
    clientAndSpouseDetails: clientAndSpouse,
    repeats,
    owner,
  });

  const calculateAmount = (): string =>
    toUSD(Number(amount), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) ?? unknown;

  return (
    <FilledCard
      className={className}
      onClick={onClick}
      heading={heading}
      titleRight={dateText}
      text={clientFullName}
      title={calculateAmount()}
      subTitle={getSubHeaderLabel(goalType)}
      titleSubScript={titleSubScript}
    />
  );
};

export default FilledGoalCard;
